import { inject, Injectable } from '@angular/core';

import { GlobalConstants } from '~core/utils/global.constants';
import { OptionalType } from '~shared/core/types/optional.type';
import { TranslationService } from './translation.service';

export interface NumberInputParseOptions {
    min?: number;
    max?: number;
    decimalPlaces?: number;
    minDecimalPlaces?: number;
    isCurrency?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class NumberInputService {
    private readonly translationService = inject(TranslationService);

    private readonly cultureDecimalSeparator: string;

    constructor() {
        // get decimal separator and thousands separator for current locale
        const localStringDecimal = (0.1).toLocaleString(this.translationService.current.culture, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        });
        this.cultureDecimalSeparator = localStringDecimal.replace(/[01]/g, '');
    }

    parse(value: OptionalType<string | number>, options: NumberInputParseOptions): [OptionalType<string>, OptionalType<number>] {
        // eslint-disable-next-line no-null/no-null
        if (value === undefined || value === null) {
            return [undefined, undefined];
        }

        let valueStr = value.toString();
        if (typeof value === 'number') {
            // for correct parsing, replace '.' with culture decimal separator
            valueStr = valueStr.replace(/\./g, this.cultureDecimalSeparator);
        }
        let valueNumber = this.parseLocalizedNumber(valueStr);

        if (valueNumber === undefined || Number.isNaN(valueNumber)) {
            return [undefined, undefined];
        }

        const min = options.min;
        const max = options.max;
        if (min !== undefined && valueNumber < min) {
            valueNumber = min;
        } else if (max !== undefined && valueNumber > max) {
            valueNumber = max;
        }

        let localizedValueNumber;

        const decimalPlaces = options.decimalPlaces;
        if (options.isCurrency) {
            localizedValueNumber = this.translationService.convertNumberToLocale(
                valueNumber,
                GlobalConstants.defaultCurrencyDecimalPlaces,
                GlobalConstants.defaultCurrencyDecimalPlaces
            );
        } else if (decimalPlaces === undefined || decimalPlaces === 0) {
            localizedValueNumber = this.translationService.convertNumberToLocale(valueNumber, 0, 0);
        } else {
            const maxAllowedDecimals = Math.min(GlobalConstants.maxDecimalPlaces, decimalPlaces);
            localizedValueNumber = this.translationService.convertNumberToLocale(
                valueNumber,
                options.minDecimalPlaces || 0,
                maxAllowedDecimals
            );
        }

        return [localizedValueNumber, this.parseLocalizedNumber(localizedValueNumber)];
    }

    private parseLocalizedNumber(value: string): number {
        // if culture ends with -CH, allow comma and dot as decimal separator, otherwise only allow culture decimal separator
        if (this.translationService.current.culture.endsWith('-CH')) {
            // remove all non-numerical characters and replace comma with dot
            value = value.replace(/[^-,.\d]/g, '');
            value = value.replace(',', '.');

            if (value === '') {
                return NaN;
            }

            return Number(value);
        }

        // remove all non-numerical characters
        value = value.replace(new RegExp(`[^-${this.cultureDecimalSeparator}\\d]`, 'g'), '');

        if (value === '') {
            return NaN;
        }

        // replace decimal sign
        value = value.replace(this.cultureDecimalSeparator, '.');

        return Number(value);
    }
}
